import { Fragment } from 'react';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

interface WizardStepperStep {
  key: string;
  titleKey: string;
}

interface WizardStepperProps {
  steps: WizardStepperStep[];
  currentIndex: number;
  onStepClick: (index: number) => void;
}

const WizardStepper = ({ steps, currentIndex, onStepClick }: WizardStepperProps) => {
  const { t } = useTranslation();
  const lastIndex = steps.length - 1;

  const circleClassName = (isActive: boolean, isDone: boolean) =>
    cn(
      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold',
      isActive
        ? 'border-brand-600 bg-brand-600 text-white'
        : isDone
        ? 'border-brand-200 bg-brand-100 text-brand-700'
        : 'border-gray-200 bg-gray-100 text-gray-400',
    );

  return (
    <div className="flex w-full items-center">
      {steps.map((step, index) => {
        const isDone = index < currentIndex;
        const isActive = index === currentIndex;
        return (
          <Fragment key={step.key}>
            {isDone ? (
              <button
                type="button"
                onClick={() => onStepClick(index)}
                className="flex shrink-0 items-center gap-2 rounded-md">
                <div className={circleClassName(isActive, isDone)}>
                  <Check size={13} />
                </div>
                <span className="whitespace-nowrap text-sm font-semibold text-brand-700">{t(step.titleKey)}</span>
              </button>
            ) : (
              <div className="flex shrink-0 items-center gap-2">
                <div className={circleClassName(isActive, isDone)}>{index + 1}</div>
                <span
                  className={cn('whitespace-nowrap text-sm', isActive ? 'font-bold text-gray-900' : 'text-gray-400')}>
                  {t(step.titleKey)}
                </span>
              </div>
            )}
            {index < lastIndex && (
              <div className={cn('mx-3 h-0.5 min-w-8 flex-1 rounded-full', isDone ? 'bg-brand-200' : 'bg-gray-200')} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default WizardStepper;
